/**
 * All themes for the design system.
 * Every component should define an object of props for each theme within their
 * themeDefaults.
 */
export const euiThemes = {
  customer: 'customer',
  corporate: 'corporate',
};

/**
 * The default theme
 * @type {string}
 */
export const defaultEuiTheme = euiThemes.customer;

function removeUndefined(obj = {}) {
  const ret = {};

  Object.keys(obj)
    .filter((key) => obj[key] !== undefined)
    .forEach((key) => (ret[key] = obj[key]));

  return ret;
}

/**
 * This mixin defines an interface for components to integrate with the EUI
 * design system themes implementation.
 */
export default {
  inject: {
    euiThemeProvider: {default: undefined},
  },
  props: {
    euiTheme: String,
  },
  data() {
    return {
      /**
       * A list of theme properties that will be used to build the merged props.
       * It is expected that components data object will define and populate this with appropriate values.
       */
      themeDefaults: {
        common: {},
        themes: {},
      },
    };
  },
  computed: {
    /**
     * The theme which is applied to the component. This is prioritised as:
     * 1: The components `eui-theme` prop
     * 2: The closes provider defined under the key 'euiThemeProvider'
     * 3: The theme defined during the installation of this plugin
     *
     * @returns {string}
     */
    _appliedTheme() {
      const $eui = this._data.$eui || {};

      return this.euiTheme || this.euiThemeProvider || $eui.theme;
    },
    /**
     * A common construction for a class based on the applied theme
     *
     * @returns {string}
     */
    _themeClass() {
      return this._appliedTheme ? `eui-theme-${this._appliedTheme}` : undefined;
    },
    /**
     * An object consisting of:
     * - All common props defined in themeDefaults
     * - All theme props defined in themeDefaults (for the applied theme)
     * - All props passed through $attrs
     *
     * @returns {{}}
     */
    _mergedProps() {
      if (!this.themeDefaults) {
        throw new Error('No themeDefaults found on this component');
      }

      const themeProps = this.themeDefaults.themes || {};

      const mergedProps = {
        ...removeUndefined(this.themeDefaults.common),
        ...removeUndefined(themeProps[this._appliedTheme]),
        ...removeUndefined(this.$props),
        ...removeUndefined(this.$attrs),
      };

      // Some quasar components mark value as required so this will ensure the
      // v-bind always includes it.
      if (!Object.prototype.hasOwnProperty.call(mergedProps, 'value')) {
        mergedProps.value = undefined;
      }

      return mergedProps;
    },
  },
};
